import React, { useState } from 'react';
import { Box, Typography, Dialog, IconButton } from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '../icons';

const FIELD_DEFS = [
  { key: 'reference_number', label: 'Reference #' },
  { key: 'transaction_date', label: 'Transaction Date' },
  { key: 'recipient_name', label: 'Recipient' },
  { key: 'recipient_number', label: 'Recipient Number' },
];

const hasReceiptData = (receiptData) =>
  Boolean(receiptData) && Object.keys(receiptData).length > 0;

const GcashReceiptPreview = ({ receiptImageUrl, receiptData, formatCurrency }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const showImage = Boolean(receiptImageUrl);
  const showData = hasReceiptData(receiptData);

  if (!showImage && !showData) {
    return null;
  }

  const openFullSize = () => {
    if (receiptImageUrl) {
      setLightboxOpen(true);
    }
  };

  const closeFullSize = () => setLightboxOpen(false);

  const dataRows = showData
    ? FIELD_DEFS.filter(({ key }) => receiptData[key])
    : [];

  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.06)',
        bgcolor: '#f9fafb',
        p: 2,
      }}
    >
      {showImage && (
        <Box
          role="button"
          tabIndex={0}
          aria-label="View GCash receipt full size"
          onClick={openFullSize}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openFullSize();
            }
          }}
          sx={{
            position: 'relative',
            display: 'block',
            cursor: 'pointer',
            borderRadius: '12px',
            overflow: 'hidden',
            '&:hover .gcash-receipt-overlay': { opacity: 1 },
            '&:focus-visible': {
              outline: '2px solid #667eea',
              outlineOffset: '2px',
            },
          }}
        >
          <Box
            component="img"
            src={receiptImageUrl}
            alt="GCash payment receipt"
            sx={{
              display: 'block',
              width: '100%',
              maxHeight: 260,
              objectFit: 'contain',
              borderRadius: '12px',
              border: '1px solid rgba(0,0,0,0.08)',
              bgcolor: 'white',
            }}
          />
          <Box
            className="gcash-receipt-overlay"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: '8px',
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              opacity: 0,
              transition: 'opacity 0.2s ease',
              pointerEvents: 'none',
            }}
          >
            <SearchIcon sx={{ fontSize: '0.85rem' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600 }}>
              View full size
            </Typography>
          </Box>
        </Box>
      )}

      {showImage && (
        <Dialog
          open={lightboxOpen}
          onClose={closeFullSize}
          maxWidth="lg"
          PaperProps={{
            sx: {
              bgcolor: 'rgba(17,24,39,0.96)',
              boxShadow: 'none',
              m: { xs: 1, sm: 2 },
              borderRadius: '12px',
              position: 'relative',
              overflow: 'hidden',
            },
          }}
        >
          <IconButton
            onClick={closeFullSize}
            aria-label="Close receipt preview"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={receiptImageUrl}
            alt="GCash payment receipt full size"
            sx={{
              display: 'block',
              maxWidth: '92vw',
              maxHeight: '88vh',
              objectFit: 'contain',
            }}
          />
        </Dialog>
      )}

      {(dataRows.length > 0 || receiptData?.amount != null) && (
        <Box sx={{ mt: showImage ? 1.75 : 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {receiptData?.amount != null && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#6b7280' }}>
                Receipt Amount
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>
                {formatCurrency ? formatCurrency(Number(receiptData.amount) || 0) : receiptData.amount}
              </Typography>
            </Box>
          )}
          {dataRows.map(({ key, label }) => (
            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', flexShrink: 0 }}>
                {label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: '#374151',
                  textAlign: 'right',
                  wordBreak: 'break-word',
                }}
              >
                {receiptData[key]}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default GcashReceiptPreview;
